"use client";

/**
 * BusyDots — "your action is running", without a spinner.
 *
 * There are two different waits in this product and only one of them has a
 * skeleton as its answer:
 *
 *   CONTENT is missing → a skeleton, shaped like the thing that will appear.
 *     `SkeletonTable`, `SkeletonStatCards` and the rest cover that.
 *
 *   AN ACTION is running → this. Nothing is being replaced: the button is
 *     already on screen and the user just pressed it. A skeleton would be wrong
 *     here — you cannot placehold a control mid-click — so the control stays put
 *     and reports that it is working.
 *
 * It occupies the SAME BOX as the `<Loader2 className="h-4 w-4 animate-spin" />`
 * it replaces, so a call site swaps one element for the other and nothing moves.
 * Pass the original's size and margin classes straight through. The dots are a
 * fifth of that box (about 3px in the usual 16px icon slot), so the same
 * component reads correctly in a 12px inline status line and a 24px upload
 * target — sized to the control it sits in, never a fixed speck.
 *
 * WHY NOT A SPINNER. A rotating circle says "something is happening" and nothing
 * else — it is the same picture whether the save took 80ms or has hung, and at
 * 16px the rotation is the only thing the eye gets. Three dots pulsing in
 * sequence read as progress at the same size, match the pulse the skeletons
 * already use, and make the whole product animate in one idiom instead of two.
 *
 * REDUCED MOTION. Under `prefers-reduced-motion` the dots stop animating and
 * hold at partial opacity: still visibly a busy state, no movement.
 */

import * as React from "react";

import { cn } from "../../utils/utils";

export interface BusyDotsProps extends React.ComponentProps<"span"> {
  /**
   * Accessible label. Defaults to nothing: inside a button whose text already
   * says "Saving…", a second announcement is noise. Pass one when the dots are
   * the ONLY indication.
   */
  label?: string;
}

/**
 * Staggered so the three dots read as a sequence rather than a single blink.
 * Values are inline because Tailwind cannot generate an arbitrary
 * `animation-delay` per child without a config entry, and this component is the
 * only place that needs them.
 */
const DELAYS = ["0ms", "150ms", "300ms"];

export function BusyDots({ label, className, ...props }: BusyDotsProps) {
  return (
    <span
      role={label ? "status" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn(
        "inline-flex h-4 w-4 shrink-0 items-center justify-center gap-[2px]",
        className,
      )}
      {...props}
    >
      {DELAYS.map((delay) => (
        <span
          key={delay}
          className="size-[20%] min-h-[2px] min-w-[2px] rounded-full bg-current opacity-40 motion-safe:animate-pulse motion-reduce:opacity-60"
          style={{ animationDelay: delay, animationDuration: "900ms" }}
        />
      ))}
      {label ? <span className="sr-only">{label}</span> : null}
    </span>
  );
}
